import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactElement } from 'react';

import { MetaBox } from '@/components/admin/MetaBox';
import { Notice } from '@/components/admin/Notice';
import { ErrorState, SkeletonRows } from '@/components/admin/states';
import {
  addDriveFile,
  deleteDriveFile,
  DRIVE_FILE_KIND_LABELS,
  DRIVE_SYNC_STATUS_LABELS,
  fetchDriveFiles,
  setDriveFileSyncStatus,
  type DriveFileKind,
  type DriveSyncStatus,
} from '@/features/meet/api';
import { ApiError } from '@/lib/api/http';
import { formatDateTime } from '@/lib/datetime';

const SYNC_STATUSES: DriveSyncStatus[] = ['unknown', 'synced', 'missing', 'error'];

/**
 * Driveファイル（文字起こし・資料）のリンクと同期状態（implementation-spec §8）。
 * 通常イベントは事務局、面談は担当者も登録可（D-366）。
 */
export function EventDriveFilesBox({
  eventId,
  meetingMode = false,
}: {
  eventId: string;
  /** 面談向けの見出し・説明（文字起こしリンク保管） */
  meetingMode?: boolean;
}): ReactElement {
  const queryClient = useQueryClient();
  const [kind, setKind] = useState<DriveFileKind>('transcript');
  const [title, setTitle] = useState('');
  const [url, setUrl] = useState('');
  const [error, setError] = useState<string | null>(null);

  const query = useQuery({
    queryKey: ['drive-files', eventId],
    queryFn: () => fetchDriveFiles(eventId),
  });

  function invalidate(): Promise<void> {
    return queryClient.invalidateQueries({ queryKey: ['drive-files', eventId] });
  }

  function onError(err: unknown): void {
    setError(err instanceof ApiError ? err.message : '操作に失敗しました。');
  }

  const addMutation = useMutation({
    mutationFn: () => addDriveFile(eventId, { kind, title: title.trim() || null, url: url.trim() }),
    onSuccess: async () => {
      setTitle('');
      setUrl('');
      setError(null);
      await invalidate();
    },
    onError,
  });

  const syncMutation = useMutation({
    mutationFn: (input: { id: string; status: DriveSyncStatus }) =>
      setDriveFileSyncStatus(eventId, input.id, input.status),
    onSuccess: async () => {
      setError(null);
      await invalidate();
    },
    onError,
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteDriveFile(eventId, id),
    onSuccess: async () => {
      setError(null);
      await invalidate();
    },
    onError,
  });

  if (query.isPending) return <SkeletonRows rows={2} />;
  if (query.isError) {
    return (
      <ErrorState
        message="Driveファイルの取得に失敗しました。"
        onRetry={() => void query.refetch()}
      />
    );
  }

  const rows = query.data;
  const busy = addMutation.isPending || syncMutation.isPending || deleteMutation.isPending;

  return (
    <MetaBox title={meetingMode ? '文字起こしデータ（リンク）' : 'Driveファイル（文字起こし・資料）'}>
      <p className="mb-2 text-wptext-muted">
        {meetingMode ? (
          <>
            面談の文字起こしは Gemini 等で<strong>システム外</strong>で生成し、
            ここに Drive 等の<strong>リンクだけ</strong>を保管します。本文は取り込みません。
          </>
        ) : (
          <>
            Google Drive にある文字起こし・資料の<strong>リンクと確認状態だけ</strong>を記録します。
            本文はこのシステムに取り込みません（閲覧の可否は Drive 側の共有設定で決まります）。
          </>
        )}
      </p>

      {error && (
        <Notice kind="error" dismissible={false}>
          {error}
        </Notice>
      )}

      {rows.length === 0 ? (
        <p className="mb-3 text-wptext-muted">まだ登録されていません。</p>
      ) : (
        <ul className="mb-3 flex flex-col gap-2">
          {rows.map((row) => (
            <li key={row.id} className="flex flex-wrap items-center gap-2">
              <span className="rounded-sm border border-wpborder px-2 py-0.5 text-wptext-muted">
                {DRIVE_FILE_KIND_LABELS[row.kind]}
              </span>
              <a
                href={row.url}
                target="_blank"
                rel="noreferrer noopener"
                className="text-wpaccent hover:underline"
              >
                {row.title ?? row.url}
              </a>
              <label className="flex items-center gap-1">
                <span className="text-wptext-muted">状態</span>
                <select
                  value={row.sync_status}
                  disabled={busy}
                  onChange={(e) =>
                    syncMutation.mutate({
                      id: row.id,
                      status: e.target.value as DriveSyncStatus,
                    })
                  }
                  className="rounded-sm border border-wpborder px-3 py-2"
                >
                  {SYNC_STATUSES.map((status) => (
                    <option key={status} value={status}>
                      {DRIVE_SYNC_STATUS_LABELS[status]}
                    </option>
                  ))}
                </select>
              </label>
              {row.last_synced_at && (
                <span className="text-wptext-muted">
                  {formatDateTime(row.last_synced_at)} 確認
                </span>
              )}
              <button
                type="button"
                disabled={busy}
                onClick={() => deleteMutation.mutate(row.id)}
                className="text-wpnotice-error hover:underline disabled:opacity-50"
              >
                削除
              </button>
            </li>
          ))}
        </ul>
      )}

      <form
        className="flex flex-wrap items-end gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          if (url.trim()) addMutation.mutate();
        }}
      >
        <label className="block">
          <span className="mb-1.5 block text-wptext-muted">種類</span>
          <select
            value={kind}
            onChange={(e) => setKind(e.target.value as DriveFileKind)}
            className="rounded-sm border border-wpborder px-3 py-2"
          >
            <option value="transcript">文字起こし</option>
            {!meetingMode && <option value="material">資料</option>}
          </select>
        </label>
        <label className="block">
          <span className="mb-1.5 block text-wptext-muted">表示名（任意）</span>
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="rounded-sm border border-wpborder px-3 py-2"
          />
        </label>
        <label className="block">
          <span className="mb-1.5 block text-wptext-muted">DriveのURL</span>
          <input
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://drive.google.com/..."
            className="min-w-[18rem] rounded-sm border border-wpborder px-3 py-2"
          />
        </label>
        <button
          type="submit"
          disabled={busy || !url.trim()}
          className="rounded-sm border border-transparent bg-wpaccent px-3.5 py-2 text-white hover:bg-wpaccent-hover disabled:opacity-50"
        >
          追加する
        </button>
      </form>
    </MetaBox>
  );
}
